import { useMemo } from 'react';
import { useDependencyGraphRFContext } from '../context';
import { System } from '../../../../types';
import { HeatmapKindEnum } from '../components/HeatmapConfig';
import { format } from 'date-fns';
import * as d3 from 'd3';
import { useTheme } from '@material-ui/core';

export const useGetSystemScoreRange = () => {
  const theme = useTheme();
  const { heatmapConfig, layout, defaultColors } =
    useDependencyGraphRFContext();

  const { systemsScores } = useMemo(() => {
    if (!heatmapConfig) {
      return {
        systemsScores: [],
      };
    }

    const systemNodes = layout.taxonomies
      .map(tax =>
        tax.root
          .descendants()
          .slice(1)
          .filter(d => d.data.type === 'system' && d.data.id !== 'placeholder'),
      )
      .flat();

    const scores = systemNodes
      .map(d => heatmapConfig.getSystemScore(d.data as System))
      .filter(
        (s): s is string | number | Date => s !== undefined && s !== null,
      );

    if (heatmapConfig.kind === HeatmapKindEnum.NUMBER) {
      const numericScores = scores.reduce((acc, s) => {
        const n = Number(s);
        if (!isNaN(n)) acc.push(n);
        return acc;
      }, [] as number[]);
      return {
        systemsScores: numericScores,
      };
    }

    if (heatmapConfig.kind === HeatmapKindEnum.DATE) {
      const dateScores = scores.reduce<Date[]>((arr, curr) => {
        if (curr instanceof Date) arr.push(curr);
        return arr;
      }, []);

      if (dateScores.length === 0) {
        return {
          systemsScores: [],
        };
      }

      const uniqueDates =
        dateScores.length > 0
          ? Array.from(
              new Set(dateScores?.map(d => format(new Date(d), 'yyyy-MM-dd'))),
            ).sort()
          : [];

      return {
        systemsScores: uniqueDates,
      };
    }

    if (heatmapConfig.kind === HeatmapKindEnum.LIST) {
      const uniqueValues = Array.from(new Set(scores.map(s => s.toString())));
      const colors = heatmapConfig.colors || defaultColors;

      let colorScale: d3.ScaleOrdinal<string, string>;

      if (colors.length >= uniqueValues.length) {
        colorScale = d3
          .scaleOrdinal<string>()
          .domain(uniqueValues)
          .range(colors);
      } else if (colors.length === 2) {
        const interpolator = d3.interpolateRgb(colors[0], colors[1]);
        const interpolatedColors = uniqueValues.map((_, i) =>
          interpolator(i / (uniqueValues.length - 1)),
        );
        colorScale = d3
          .scaleOrdinal<string>()
          .domain(uniqueValues)
          .range(interpolatedColors);
      } else {
        colorScale = d3
          .scaleOrdinal<string>()
          .domain(uniqueValues)
          .range(colors);
      }

      const values = [
        ...uniqueValues.map(value => ({ value, color: colorScale(value) })),
        { value: 'No value', color: theme.palette.grey[100] },
      ];

      return { systemsScores: values };
    }

    return {
      systemsScores: [],
    };
  }, [heatmapConfig, layout.taxonomies, defaultColors, theme]);

  return { systemsScores };
};
